import React, { useEffect, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'react-flow-renderer';
import { motion } from 'framer-motion';
import './SkillGraph.css';

const SkillGraph = ({ skills = [], onSkillClick }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);

  useEffect(() => {
    if (skills.length === 0) return;

    // Convert skills to graph nodes
    const graphNodes = skills.map((skill, index) => {
      const status = skill.status || 'locked';
      const row = Math.floor(index / 3);
      const col = index % 3;

      return {
        id: skill._id || skill.id || String(index),
        type: 'custom',
        position: { x: col * 250, y: row * 200 },
        data: {
          label: skill.name,
          icon: skill.icon || '📚',
          estimatedHours: skill.estimatedHours || 0,
          difficulty: skill.difficulty || 'intermediate',
          status,
          proficiency: skill.proficiency || 0,
          onClick: () => handleSkillClick(skill),
        },
        className: `skill-node skill-node-${status}`,
      };
    });

    // Create edges for prerequisites
    const graphEdges = [];
    skills.forEach((skill, index) => {
      if (skill.prerequisites && Array.isArray(skill.prerequisites)) {
        skill.prerequisites.forEach((prereqId) => {
          const prereqIndex = skills.findIndex(s => s._id === prereqId || s.id === prereqId);
          if (prereqIndex !== -1) {
            graphEdges.push({
              id: `e${prereqIndex}-${index}`,
              source: String(prereqIndex),
              target: String(index),
              type: 'smoothstep',
              animated: skill.status === 'in-progress',
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: skill.status === 'completed' ? '#10b981' : '#94a3b8',
              },
              style: {
                stroke: skill.status === 'completed' ? '#10b981' : '#94a3b8',
                strokeWidth: 2,
              },
            });
          }
        });
      }
    });

    setNodes(graphNodes);
    setEdges(graphEdges);
  }, [skills]);

  const handleSkillClick = (skill) => {
    setSelectedSkill(skill);
    if (onSkillClick) {
      onSkillClick(skill);
    }
  };

  const CustomNode = ({ data }) => {
    const { label, icon, estimatedHours, difficulty, status, proficiency, onClick } = data;

    const getStatusColor = () => {
      switch (status) {
        case 'completed':
          return '#10b981';
        case 'in-progress':
          return '#3b82f6';
        case 'available':
          return '#8b5cf6';
        case 'locked':
          return '#64748b';
        default:
          return '#64748b';
      }
    };

    const getStatusIcon = () => {
      switch (status) {
        case 'completed':
          return '✓';
        case 'in-progress':
          return '▶';
        case 'available':
          return '○';
        case 'locked':
          return '🔒';
        default:
          return '○';
      }
    };

    return (
      <motion.div
        className={`custom-skill-node ${status}`}
        onClick={onClick}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{ borderColor: getStatusColor() }}
      >
        <div className="skill-node-icon" style={{ backgroundColor: getStatusColor() }}>
          {icon}
        </div>
        <div className="skill-node-content">
          <div className="skill-node-status">{getStatusIcon()}</div>
          <h4 className="skill-node-title">{label}</h4>
          <div className="skill-node-meta">
            <span className="skill-hours">⏱️ {estimatedHours}h</span>
            <span className={`skill-difficulty skill-${difficulty}`}>
              {difficulty}
            </span>
          </div>
          {status === 'in-progress' && proficiency > 0 && (
            <div className="skill-progress-bar">
              <div
                className="skill-progress-fill"
                style={{ width: `${proficiency}%` }}
              />
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const nodeTypes = {
    custom: CustomNode,
  };

  return (
    <div className="skill-graph-container">
      <div className="skill-graph-header">
        <h3>Your Learning Path</h3>
        <div className="skill-graph-legend">
          <span className="legend-item">
            <span className="legend-dot completed"></span>
            Completed
          </span>
          <span className="legend-item">
            <span className="legend-dot in-progress"></span>
            In Progress
          </span>
          <span className="legend-item">
            <span className="legend-dot available"></span>
            Available
          </span>
          <span className="legend-item">
            <span className="legend-dot locked"></span>
            Locked
          </span>
        </div>
      </div>

      <div className="skill-graph-wrapper">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-left"
        >
          <Background color="#1e293b" gap={16} />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              if (node.className?.includes('completed')) return '#10b981';
              if (node.className?.includes('in-progress')) return '#3b82f6';
              if (node.className?.includes('available')) return '#8b5cf6';
              return '#64748b';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
          />
        </ReactFlow>
      </div>

      {selectedSkill && (
        <motion.div
          className="skill-detail-panel"
          initial={{ x: 300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 300, opacity: 0 }}
        >
          <button
            className="close-panel"
            onClick={() => setSelectedSkill(null)}
          >
            ✕
          </button>
          <div className="skill-detail-icon">{selectedSkill.icon}</div>
          <h3>{selectedSkill.name}</h3>
          <p className="skill-description">{selectedSkill.description || 'Master this essential skill'}</p>
          
          <div className="skill-detail-stats">
            <div className="stat">
              <span className="stat-label">Estimated Time</span>
              <span className="stat-value">{selectedSkill.estimatedHours}h</span>
            </div>
            <div className="stat">
              <span className="stat-label">Difficulty</span>
              <span className={`stat-value skill-${selectedSkill.difficulty}`}>
                {selectedSkill.difficulty}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Status</span>
              <span className="stat-value">{selectedSkill.status}</span>
            </div>
          </div>

          {selectedSkill.prerequisites && selectedSkill.prerequisites.length > 0 && (
            <div className="skill-prerequisites">
              <h4>Prerequisites</h4>
              <ul>
                {selectedSkill.prerequisites.map((prereq, idx) => (
                  <li key={idx}>{skills.find(s => s._id === prereq)?.name || prereq}</li>
                ))}
              </ul>
            </div>
          )}

          {selectedSkill.status === 'available' && (
            <button className="btn-primary btn-start-skill">
              Start Learning
            </button>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default SkillGraph;
